import { useEffect, useRef } from 'react';
import trackerProfiler from '@openreplay/tracker-profiler';
import Tracker from '@openreplay/tracker';

const PROJECT_KEY = import.meta.env.REACT_APP_OPENREPLAY_PROJECT_KEY || '';

export const TRACKER_EVENT_KEYS = {
  WS_LATENCY: 'ws_latency',
  WS_CONNECTION_ERROR: 'ws_connection_error',
};

let trackerInstance: Tracker | null = null;
let trackerStarted = false;
let profilerInstance: any = null;

export function getObscuredProps() {
  return { 'data-openreplay-obscured': true };
}

function getOrCreateTracker() {
  const blockedDomains = ['testapp.immersive-finance.com'];
  const currentHostname = window.location.hostname;

  if (blockedDomains.includes(currentHostname) || !PROJECT_KEY || PROJECT_KEY === '') {
    return null;
  }

  if (!trackerInstance) {
    trackerInstance = new Tracker({
      projectKey: PROJECT_KEY,
      defaultInputMode: 0,
      obscureTextNumbers: true,
      obscureInputNumbers: true,
      capturePerformance: true,
    });
    profilerInstance = trackerInstance.use(trackerProfiler());
  }

  return trackerInstance;
}

export function getProfiler() {
  getOrCreateTracker();
  return profilerInstance;
}

export function trackWsConnectionError(reason: string, details?: Record<string, unknown>) {
  const tracker = getOrCreateTracker();

  if (!tracker) {
    return;
  }

  try {
    tracker.event(
      TRACKER_EVENT_KEYS.WS_CONNECTION_ERROR,
      JSON.stringify({
        reason,
        ...details,
      }),
    );
  } catch (err) {
    console.error('Error sending ws_connection_error event:', err);
  }
}

export function startSessionTracker(userEmail?: string) {
  const tracker = getOrCreateTracker();

  if (!trackerStarted && userEmail) {
    trackerStarted = true;

    if (tracker) {
      tracker.start({ userID: userEmail });
    }

    console.log('tracker started here');
  }
}

export function getSessionTracker() {
  const tracker = getOrCreateTracker();
  return {
    tracker,
    handleWsMessage: (message: MessageEvent<any>, onMessage: (msg: MessageEvent<any>) => void) => {
      const canCapture = !!message?.data && message.data?.method !== 'update';
      if (canCapture && tracker) {
        const string = Math.random().toString(36).slice(2, 46);
        try {
          const wsHook = tracker.trackWs('my_ws_channel');
          if (wsHook) {
            wsHook(message.data?.method, string, 'down');
          }
        } catch (err: any) {
          console.log(err);
        }
      }
      onMessage(message);
    },
  };
}

export function useSessionTracker(userEmail?: string) {
  const trackerRef = useRef<Tracker | null>(null);

  useEffect(() => {
    const tracker = getOrCreateTracker();
    trackerRef.current = tracker;

    startSessionTracker(userEmail);
  }, [userEmail]);

  return {
    tracker: trackerRef.current,
    handleWsMessage: getSessionTracker().handleWsMessage,
    profiler: getProfiler(),
  };
}
